import { useEffect, useRef, useState } from 'react';
import { Button, ConfigProvider, Drawer, Form, Input, InputNumber, Radio, Select, Switch } from 'antd';
import heIL from 'antd/locale/he_IL';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ANY_COACH,
  ANY_HUG,
  BoxSettingsSchema,
  SalaryComponentsSchema,
  isValidBusinessId,
  type BoxSettings,
  type CancelReasonPayRule,
  type HugRole,
  type HugimPercentRow,
  type CategoryRateRule,
  type SeriesRateRule,
} from '@salaries/shared';
import { updateBoxSettings } from '../../api/boxSettings';
import {
  deleteHugimAddonPercent,
  deleteHugimPercent,
  getHugimAddonSettings,
  getHugimSettings,
  upsertHugimAddonPercent,
  upsertHugimPercent,
} from '../../api/hugim';
import { AddonsFields } from './AddonsFields';
import { PercentSumWarning } from './PercentSumWarning';
import { HugimOverridesSection } from './HugimOverridesSection';
import { getBoxSalaryDefaults, updateBoxSalaryDefaults } from '../../api/salaryDefaults';
import { updateCancelReasonRules } from '../../api/cancelReasons';
import { Loader } from '../../components/Loader';
import { SettingCard, SettingsAccordion } from '../../components/SettingCard';
import { CategoryRateFields } from '../staff-settings/CategoryRateFields';
import { SeriesRateFields } from '../staff-settings/SeriesRateFields';
import { InvoicePolicyField } from './InvoicePolicyField';
import { InvoiceAmountCheckField } from './InvoiceAmountCheckField';
import { PerChildFeeFields } from './PerChildFeeFields';
import { PnlUnitsCard } from './PnlUnitsCard';
import { CancelledClassPayFields } from './CancelledClassPayFields';
import { VolumeBonusFields } from './VolumeBonusFields';
import { DailyClassAdjustmentFields } from './DailyClassAdjustmentFields';
import { ConversionFields } from './ConversionBonusFields';
import { CoachEmailTextFields } from './CoachEmailTextFields';
import {
  BaseFields,
  DEFAULT_COMPONENT_FLAT,
  EmptyClassPayFields,
  TravelFields,
  WeekendFields,
  componentsToFlat,
  flatToComponents,
  type ComponentFlat,
} from './salaryComponentFields';

const CALC_METHOD_OPTIONS = [
  { value: 'checkin', label: "לפי צ'ק-אין" },
  { value: 'registered', label: 'לפי רשומים' },
  { value: 'checkin_with_late_cancel', label: "לפי צ'ק-אין + ביטולים מאוחרים" },
  { value: 'registered_with_late_cancel', label: 'לפי רשומים + ביטולים מאוחרים' },
] as const;

// The box's pay mechanisms — independently toggleable. A box may run any
// combination; a staff member is then individually assigned to one or more of
// whichever the box has enabled (see StaffSalaryEditDrawer), consolidated into
// one salary line per staff (see payroll-mechanisms.service.ts server-side).
const MECHANISM_TOGGLES: { field: 'mech_group_classes' | 'mech_hugim' | 'mech_time_clock'; label: string }[] = [
  { field: 'mech_group_classes', label: 'שיעורים (קבוצתי)' },
  { field: 'mech_hugim', label: 'חוגים (אחוז מהתשלום)' },
  { field: 'mech_time_clock', label: 'שעון נוכחות (עובדים רגילים)' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  initial: BoxSettings;
  onSaved: (s: BoxSettings) => void;
}

// The drawer's single form covers both the box settings (reply-to + calc method)
// and the GENERAL salary defaults (base / weekend / travel) inlined as a section
// — no more drawer-in-drawer. Both are saved together on submit.
interface FormShape extends ComponentFlat {
  reply_to_email: string | null;
  mech_group_classes: boolean;
  mech_hugim: boolean;
  mech_time_clock: boolean;
  class_calc_method: BoxSettings['class_calc_method'];
  invoice_policy: BoxSettings['invoice_policy'];
  invoice_amount_check: BoxSettings['invoice_amount_check'];
  business_id: string | null;
  accountant_email: string | null;
  approval_deadline_days: number | null;
  forward_invoices_to_expenses: boolean;
  /** Global default % for hugim boxes (the (any-coach, any-hug) row). */
  hugim_default_percent: number;
  /** The secondary staff member's global default (null = none → 0%). */
  hugim_secondary_default_percent: number | null;
  /** חוג לכל ילד (hugim boxes): on/off, the identifying fee, the coach's cut. */
  per_child_fee_enabled: boolean;
  per_child_fee_amount: number | null;
  per_child_fee_percent: number;
  /** מוצרים נלווים (hugim boxes): the default % (null = none — the
   *  per-course list saves on its own) and when the payout lands. */
  addons_default_percent: number | null;
  addons_secondary_default_percent: number | null;
  addons_payment_method: BoxSettings['addons_payment_method'];
  /** רישום לקורס שטרם התחיל (hugim boxes): when a prepaid payment starts paying. */
  prepaid_payout_start: BoxSettings['prepaid_payout_start'];
  /** אופן חישוב השכר (hugim boxes): which report column the course % applies to. */
  hugim_pay_base: BoxSettings['hugim_pay_base'];
  /** לכלול מפגשי ניסיון (hugim boxes): whether paid trial sessions pay. */
  hugim_include_trials: boolean;
  /** לכלול מפגשים חד-פעמיים (hugim boxes): whether paid one-time sessions pay. */
  hugim_include_onetime: boolean;
  /** The coach email's box-written intro + signature (see coach-email.ts). */
  coach_email_intro: string | null;
  coach_email_signature: string | null;
}

/** אופן חישוב השכר — in the order of the columns in Arbox's "הכנסות מחוגים
 *  (לפי תאריך תשלום)" report. The last one is computed, not a column, so it
 *  goes last on purpose (AOAT-108 exception). */
const HUGIM_PAY_BASE_OPTIONS: { value: BoxSettings['hugim_pay_base']; label: string; help: string }[] = [
  {
    value: 'receipt_amount',
    label: 'סכום בקבלה',
    help: 'הסכום שנרשם בקבלה עבור הפריט, אחרי כל ההנחות. בדוח "הכנסות מחוגים (לפי תאריך תשלום)" עמודה זו מוצגת כולל נלווים, ובחישוב השכר הם מנוכים ממנה.',
  },
  {
    value: 'item_price',
    label: 'מחיר הפריט',
    help: 'מחיר המחירון המלא של החוג, לפני כל הנחה ולפני חישוב יחסי. איש הצוות יקבל אחוז מהמחיר המלא גם אם הלקוח שילם פחות, והחזרים כספיים אינם מקוזזים.',
  },
  {
    value: 'value',
    label: 'שווי',
    help: 'מחיר הפריט בניכוי ההנחה הקבועה שניתנה ללקוח. אינו מושפע מהנחה חד-פעמית, מחיוב חלקי או מהצטרפות באמצע תקופה - במקרים אלה הסכום יהיה גבוה ממה שנגבה בפועל.',
  },
  {
    value: 'actually_paid',
    label: 'שולם בפועל',
    help: 'הסכום שנגבה בפועל מהלקוח עבור הפריט, אחרי הנחה קבועה, אחרי הנחה חד-פעמית ואחרי חישוב יחסי.',
  },
  {
    value: 'actually_paid_plus_discount',
    label: 'שולם בפועל בתוספת הנחה',
    help: 'הסכום שנגבה בפועל מהלקוח עבור הפריט, בתוספת ההנחה שניתנה לו - קבועה או חד-פעמית. החישוב היחסי נשמר, וההנחה אינה מקטינה את שכר איש הצוות.',
  },
];

export function BoxSettingsDrawer({ open, onClose, initial, onSaved }: Props) {
  const [form] = Form.useForm<FormShape>();
  const queryClient = useQueryClient();

  const hasGroupClasses = Form.useWatch('mech_group_classes', form) ?? initial.mech_group_classes;
  const hasHugim = Form.useWatch('mech_hugim', form) ?? initial.mech_hugim;
  const hasTimeClock = Form.useWatch('mech_time_clock', form) ?? initial.mech_time_clock;
  // Watched values → the collapsed setting-card summary lines.
  const calcMethod = Form.useWatch('class_calc_method', form);
  const categoryRates = Form.useWatch('category_rates', form) as CategoryRateRule[] | undefined;
  const seriesRates = Form.useWatch('series_rates', form) as SeriesRateRule[] | undefined;
  const invoicePolicy = Form.useWatch('invoice_policy', form);
  const baseType = Form.useWatch('base_type', form);
  const weekendChoice = Form.useWatch('weekend_choice', form);
  const travelType = Form.useWatch('travel_type', form);
  const conversionEnabled = Form.useWatch('conversion_enabled', form);
  const perChildFeeEnabled = Form.useWatch('per_child_fee_enabled', form);
  const perChildFeeAmount = Form.useWatch('per_child_fee_amount', form);
  const perChildFeePercent = Form.useWatch('per_child_fee_percent', form);
  const addonsDefaultPercent = Form.useWatch('addons_default_percent', form);
  const hugimDefaultPercent = Form.useWatch('hugim_default_percent', form);
  const hugimSecondaryDefaultPercent = Form.useWatch('hugim_secondary_default_percent', form);
  const addonsPaymentMethod = Form.useWatch('addons_payment_method', form);
  const prepaidPayoutStart = Form.useWatch('prepaid_payout_start', form);
  const hugimIncludeTrials = Form.useWatch('hugim_include_trials', form);
  const hugimIncludeOnetime = Form.useWatch('hugim_include_onetime', form);
  const hugimPayBase = Form.useWatch('hugim_pay_base', form);
  const emptyClassPayEnabled = Form.useWatch('empty_class_pay_enabled', form);
  const volumeBonusEnabled = Form.useWatch('volume_bonus_enabled', form);
  const volumeBonusTiers = Form.useWatch('volume_bonus_tiers', form);
  const dailyClassAdjustmentEnabled = Form.useWatch('daily_class_adjustment_enabled', form);
  const dailyClassAdjustmentTiers = Form.useWatch('daily_class_adjustment_tiers', form);
  const cancelledClassPayEnabled = Form.useWatch('cancelled_class_pay_enabled', form);
  const coachEmailIntro = Form.useWatch('coach_email_intro', form);
  const coachEmailSignature = Form.useWatch('coach_email_signature', form);
  const coachEmailSummary =
    coachEmailIntro?.trim() || coachEmailSignature?.trim() ? 'מותאם אישית' : 'ברירת מחדל';
  // The per-reason cancellation rules are a list, not a form field. null until
  // the card has loaded them — saving skips them entirely in that case, so
  // closing the drawer without opening the card can't wipe the box's rules.
  const [cancelRules, setCancelRules] = useState<CancelReasonPayRule[] | null>(null);
  // Which card is open, so the reason catalog (a report call) is only fetched
  // when the operator actually opens that card.
  const [openCard, setOpenCard] = useState<string | null>(null);
  const calcLabel = CALC_METHOD_OPTIONS.find((o) => o.value === calcMethod)?.label ?? '';
  const invoiceLabel =
    invoicePolicy === 'required' ? 'חובה' : invoicePolicy === 'none' ? 'לא נאספות' : 'רשות';
  const travelSummary =
    travelType === 'fixed_monthly'
      ? 'סכום חודשי'
      : travelType === 'per_workday'
        ? 'לפי יום עבודה'
        : travelType === 'per_km'
          ? 'לפי קילומטר'
          : 'ללא';
  const weekendSummary =
    weekendChoice === 'percent' ? 'אחוז מהשכר' : weekendChoice === 'fixed' ? 'סכום קבוע' : 'כבוי';

  // The general salary defaults are loaded alongside the drawer opening.
  const defaultsQuery = useQuery({
    queryKey: ['salary-defaults'],
    queryFn: getBoxSalaryDefaults,
    enabled: open,
  });

  // Hugim percentages — only needed to seed the global-default field.
  const hugimQuery = useQuery({
    queryKey: ['hugim-percentages'],
    queryFn: getHugimSettings,
    enabled: open,
  });

  // Add-on percentages — only needed to seed the default field.
  const addonsQuery = useQuery({
    queryKey: ['hugim-addon-percentages'],
    queryFn: getHugimAddonSettings,
    enabled: open,
  });

  // Seed the form ONCE per opening, when everything it reads has loaded. The
  // per-course lists save on their own and refetch the percentages; re-seeding
  // then would wipe whatever the operator had typed but not yet saved.
  const seeded = useRef(false);
  useEffect(() => {
    if (!open) seeded.current = false;
  }, [open]);
  useEffect(() => {
    if (open && !seeded.current && defaultsQuery.data && hugimQuery.data && addonsQuery.data) {
      seeded.current = true;
      form.setFieldsValue({
        reply_to_email: initial.reply_to_email,
        mech_group_classes: initial.mech_group_classes,
        mech_hugim: initial.mech_hugim,
        mech_time_clock: initial.mech_time_clock,
        class_calc_method: initial.class_calc_method,
        invoice_policy: initial.invoice_policy,
        invoice_amount_check: initial.invoice_amount_check,
        business_id: initial.business_id,
        accountant_email: initial.accountant_email,
        approval_deadline_days: initial.approval_deadline_days,
        forward_invoices_to_expenses: initial.forward_invoices_to_expenses,
        per_child_fee_enabled: initial.per_child_fee_enabled,
        per_child_fee_amount: initial.per_child_fee_amount,
        per_child_fee_percent: initial.per_child_fee_percent,
        coach_email_intro: initial.coach_email_intro,
        coach_email_signature: initial.coach_email_signature,
        hugim_default_percent: hugimQuery.data?.default_percent ?? 0,
        hugim_secondary_default_percent: hugimQuery.data?.secondary_default_percent ?? null,
        addons_default_percent: addonsQuery.data?.default_percent ?? null,
        addons_secondary_default_percent: addonsQuery.data?.secondary_default_percent ?? null,
        addons_payment_method: initial.addons_payment_method,
        prepaid_payout_start: initial.prepaid_payout_start,
        hugim_pay_base: initial.hugim_pay_base,
        hugim_include_trials: initial.hugim_include_trials,
        hugim_include_onetime: initial.hugim_include_onetime,
        ...componentsToFlat(defaultsQuery.data),
      });
    }
  }, [open, initial, defaultsQuery.data, hugimQuery.data, addonsQuery.data, form]);

  const mutation = useMutation({
    mutationFn: async ({ payload, values }: { payload: BoxSettings; values: FormShape }) => {
      // Save the box settings + the general salary defaults together.
      const settings = await updateBoxSettings(payload);
      // Mechanisms are independent now — a box can run more than one at once,
      // so each mechanism's own settings save whenever IT is on, not exclusively.
      if (payload.mech_hugim) {
        await upsertHugimPercent({
          coach_arbox_users_boxes_id: ANY_COACH,
          hug_key: ANY_HUG,
          percent: values.hugim_default_percent ?? 0,
        });
        // The optional defaults: blank clears the row (→ 0% on every course
        // without its own). Off the store — the card may never have been expanded.
        const saveOptionalDefault = async (
          value: unknown,
          had: number | null | undefined,
          role: HugRole,
          api: {
            upsert: (row: HugimPercentRow) => Promise<unknown>;
            remove: (coach: number, hug: string, role: HugRole) => Promise<unknown>;
          },
        ) => {
          if (value == null) {
            if (had != null) await api.remove(ANY_COACH, ANY_HUG, role);
          } else {
            await api.upsert({ coach_arbox_users_boxes_id: ANY_COACH, hug_key: ANY_HUG, percent: Number(value), role });
          }
        };
        const courseApi = { upsert: upsertHugimPercent, remove: deleteHugimPercent };
        const addonApi = { upsert: upsertHugimAddonPercent, remove: deleteHugimAddonPercent };
        await saveOptionalDefault(
          form.getFieldValue('hugim_secondary_default_percent'),
          hugimQuery.data?.secondary_default_percent,
          'secondary',
          courseApi,
        );
        await saveOptionalDefault(
          form.getFieldValue('addons_default_percent'),
          addonsQuery.data?.default_percent,
          'primary',
          addonApi,
        );
        await saveOptionalDefault(
          form.getFieldValue('addons_secondary_default_percent'),
          addonsQuery.data?.secondary_default_percent,
          'secondary',
          addonApi,
        );
      }
      if (payload.mech_group_classes || payload.mech_time_clock) {
        // Both use the general salary defaults (base salary + travel);
        // time_clock never sets the class-specific ones, which flatToComponents
        // already leaves off/zeroed for it.
        await updateBoxSalaryDefaults(flatToComponents(values));
      }
      // Cancelled-class pay is class-specific — only meaningful for group_classes.
      if (payload.mech_group_classes && cancelRules) await updateCancelReasonRules(cancelRules);
      return settings;
    },
    onSuccess: (saved) => {
      toast.success('ההגדרות נשמרו');
      void queryClient.invalidateQueries({ queryKey: ['salary-defaults'] });
      void queryClient.invalidateQueries({ queryKey: ['cancel-reasons'] });
      void queryClient.invalidateQueries({ queryKey: ['salary-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['hugim-percentages'] });
      void queryClient.invalidateQueries({ queryKey: ['hugim-addon-percentages'] });
      void queryClient.invalidateQueries({ queryKey: ['payroll-report'] });
      onSaved(saved);
      onClose();
    },
    onError: (err) => toast.error((err as Error).message || 'שגיאה בשמירת ההגדרות'),
  });

  const onFinish = (values: FormShape) => {
    // The mechanism switches are always mounted (their card never conditionally
    // unmounts), so values.mech_* are reliable. The calc-method field only
    // renders when group_classes is on — fall back to the store/`initial` when
    // it wasn't rendered this session, same reasoning as before.
    const boxParsed = BoxSettingsSchema.safeParse({
      pay_period: { mode: 'calendar_month', custom_start_day: null },
      reply_to_email: values.reply_to_email ?? null,
      // box_type is legacy (see box-settings.ts) — kept sane for back-compat,
      // but dispatch now reads the mech_* flags below, not this. With multiple
      // mechanisms on, hugim wins the legacy field (mirrors primaryMechanism's
      // priority) purely for cosmetic back-compat; nothing reads it anymore.
      box_type: values.mech_hugim ? 'hugim' : 'group_classes',
      mech_group_classes: !!values.mech_group_classes,
      mech_hugim: !!values.mech_hugim,
      mech_time_clock: !!values.mech_time_clock,
      class_calc_method: form.getFieldValue('class_calc_method') ?? initial.class_calc_method,
      invoice_policy: values.invoice_policy ?? initial.invoice_policy,
      // A box that stopped collecting invoices has nothing to verify — keep the
      // stored mode consistent with the policy rather than leaving a dangling
      // 'block' that would spring back to life if invoices were re-enabled.
      invoice_amount_check:
        (values.invoice_policy ?? initial.invoice_policy) === 'none'
          ? 'none'
          : (values.invoice_amount_check ?? initial.invoice_amount_check),
      // Off the store: the field sits in a section that's hidden (not unmounted)
      // when the box doesn't collect invoices — never let that wipe the ח״פ.
      business_id: form.getFieldValue('business_id') ?? initial.business_id ?? null,
      accountant_email: values.accountant_email ?? null,
      approval_deadline_days: values.approval_deadline_days ?? null,
      forward_invoices_to_expenses: !!values.forward_invoices_to_expenses,
      // Read off the STORE, not `values`: this card only renders on a hugim box,
      // and on any other box `values` wouldn't carry it — which would let Zod's
      // defaults quietly switch the setting off on every save.
      per_child_fee_enabled: !!form.getFieldValue('per_child_fee_enabled'),
      per_child_fee_amount: form.getFieldValue('per_child_fee_amount') ?? null,
      per_child_fee_percent: Number(form.getFieldValue('per_child_fee_percent') ?? 0),
      // Off the store for the same reason as per_child_fee_* above.
      addons_payment_method:
        form.getFieldValue('addons_payment_method') ?? initial.addons_payment_method ?? 'spread',
      prepaid_payout_start:
        form.getFieldValue('prepaid_payout_start') ?? initial.prepaid_payout_start ?? 'course_start',
      hugim_pay_base:
        form.getFieldValue('hugim_pay_base') ?? initial.hugim_pay_base ?? 'receipt_amount',
      hugim_include_trials:
        form.getFieldValue('hugim_include_trials') ?? initial.hugim_include_trials ?? true,
      hugim_include_onetime:
        form.getFieldValue('hugim_include_onetime') ?? initial.hugim_include_onetime ?? true,
      coach_email_intro: values.coach_email_intro?.trim() || null,
      coach_email_signature: values.coach_email_signature?.trim() || null,
    });
    if (!boxParsed.success) {
      toast.error(boxParsed.error.issues[0]?.message || 'נתונים שגויים');
      return;
    }
    // The general salary defaults apply to group_classes and time_clock boxes.
    if (values.mech_group_classes || values.mech_time_clock) {
      const compParsed = SalaryComponentsSchema.safeParse(flatToComponents(values));
      if (!compParsed.success) {
        toast.error(compParsed.error.issues[0]?.message || 'נתונים שגויים');
        return;
      }
    }
    // One payload, validated once, then saved — the parse and the request used
    // to be two hand-kept copies of the same object, which is how they drifted.
    mutation.mutate({ payload: boxParsed.data, values });
  };

  return (
    <Drawer
      // Arbox sidebar header title is text-heading-1 (20/600), same as modals
      // and the Arbox parent app — matches the expenses add-on.
      title={
        <span className="text-main-text" style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2 }}>
          הגדרות שכר
        </span>
      }
      placement="left"
      open={open}
      onClose={onClose}
      width={560}
      closable={false}
      extra={
        <button
          type="button"
          aria-label="סגור"
          onClick={onClose}
          className="flex items-center justify-center w-8 h-8 rounded text-secondary-text hover:bg-grey-bg hover:text-main-text text-2xl leading-none"
        >
          ×
        </button>
      }
      // Arbox sidebar: no divider under the title, no border above the footer.
      // Footer lives in the antd `footer` prop so it stays sticky while the body
      // scrolls between the (sticky) header and (sticky) footer. Tight header +
      // small body top-padding so the scrollable content sits close to the title.
      styles={{
        header: { borderBottom: 'none', paddingTop: 16, paddingBottom: 4 },
        body: { paddingTop: 8 },
        // Give the cancel/save buttons air above and below (lift them off the edge).
        footer: { borderTop: 'none', padding: '16px 24px 20px' },
      }}
      footer={
        <div className="flex items-center justify-end gap-3" dir="rtl">
          <Button onClick={onClose}>ביטול</Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={mutation.isPending}
            disabled={defaultsQuery.isLoading}
          >
            שמירה
          </Button>
        </div>
      }
    >
      <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
        {defaultsQuery.isLoading ? (
          <div className="flex justify-center py-10">
            <Loader size={48} />
          </div>
        ) : (
          <Form<FormShape>
            form={form}
            layout="vertical"
            initialValues={{
              reply_to_email: initial.reply_to_email,
              class_calc_method: initial.class_calc_method,
              ...DEFAULT_COMPONENT_FLAT,
            }}
            onFinish={onFinish}
            requiredMark={false}
          >
            <SettingsAccordion onOpenChange={setOpenCard}>
            {/* ── Pay mechanisms — independently toggleable, a box may run any
                combination at once (see MECHANISM_TOGGLES) ─────────────── */}
            <SettingCard
              id="mechanism"
              title="שיטות תשלום שכר"
              summary={MECHANISM_TOGGLES.filter((t) =>
                t.field === 'mech_group_classes' ? hasGroupClasses : t.field === 'mech_hugim' ? hasHugim : hasTimeClock,
              )
                .map((t) => t.label)
                .join(' + ')}
            >
              <div className="flex flex-col gap-3">
                {MECHANISM_TOGGLES.map((t) => (
                  <div key={t.field} className="flex items-center justify-between gap-4">
                    <span className="text-label text-main-text">{t.label}</span>
                    <Form.Item name={t.field} valuePropName="checked" className="mb-0" noStyle>
                      <Switch />
                    </Form.Item>
                  </div>
                ))}
              </div>
              <div className="text-small text-secondary-text mt-3">
                לפחות שיטה אחת חייבת להיות פעילה. איש צוות המשויך ליותר משיטה אחת (למשל חוגים + שעון
                נוכחות) מקבל שורת שכר אחת מאוחדת המשלבת את שתי השיטות.
              </div>
            </SettingCard>

            {hasHugim && (
              /* ── אופן חישוב השכר: which report column the course % is
                  applied to. Same order as the columns in Arbox's report. ── */
              <SettingCard
                id="hugim_pay_base"
                title="אופן חישוב השכר"
                summary={HUGIM_PAY_BASE_OPTIONS.find((o) => o.value === hugimPayBase)?.label}
              >
                <div className="text-small text-secondary-text mb-3">
                  ההגדרה קובעת את בסיס החישוב עבור החוג עצמו בלבד. התשלום על מוצרים נלווים נקבע בהגדרה
                  נפרדת ומתווסף לשכר.
                </div>
                <Form.Item name="hugim_pay_base" className="mb-0">
                  <Radio.Group className="flex flex-col gap-3">
                    {HUGIM_PAY_BASE_OPTIONS.map((o) => (
                      <Radio key={o.value} value={o.value}>
                        <div className="text-label text-main-text">{o.label}</div>
                        <div className="text-small text-secondary-text">{o.help}</div>
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
              </SettingCard>
            )}

            {hasHugim && (
              /* ── Hugim: global default percentage ─────────────────── */
              <SettingCard id="hugim" title="אחוז מהתשלום לחוגים">
                <Form.Item
                  name="hugim_default_percent"
                  label="אחוז ברירת מחדל לאיש צוות ראשי מהתשלום לחוג (%)"
                  className="mb-2"
                >
                  <InputNumber min={0} max={100} style={{ width: 160 }} dir="ltr" />
                </Form.Item>
                <Form.Item
                  name="hugim_secondary_default_percent"
                  label="אחוז ברירת מחדל לאיש צוות משני מהתשלום לחוג (%)"
                  className="mb-0"
                  normalize={(v) => (v === '' || v == null ? null : v)}
                >
                  <InputNumber min={0} max={100} style={{ width: 160 }} dir="ltr" />
                </Form.Item>
                <PercentSumWarning primary={hugimDefaultPercent} secondary={hugimSecondaryDefaultPercent} />
                <div className="text-small text-secondary-text mt-2 mb-3">
                  ברירת המחדל חלה על כל החוגים ואנשי הצוות. ניתן לשנות לפי חוג או לפי איש צוות בהגדרות
                  למטה. שינויים ברמת איש צוות או חוג גוברים על ההגדרה הכללית.
                </div>
                <HugimOverridesSection />
              </SettingCard>
            )}

            {hasHugim && (
              /* ── רישום לקורס שטרם התחיל: a payment made before the course
                  starts — pay from the course start or from the charge. ──── */
              <SettingCard
                id="prepaid_payout_start"
                title="רישום לקורס שטרם התחיל"
                summary={
                  prepaidPayoutStart === 'charge_date' ? 'החל ממועד החיוב' : 'מתחילת הקורס'
                }
              >
                <Form.Item name="prepaid_payout_start" className="mb-2">
                  <Radio.Group className="flex flex-col gap-3">
                    <Radio value="course_start">מתחילת הקורס</Radio>
                    <Radio value="charge_date">החל ממועד החיוב</Radio>
                  </Radio.Group>
                </Form.Item>
                <div className="text-small text-secondary-text">
                  כשלקוח משלם לפני תחילת הקורס — מאיזה חודש מתחיל התשלום לאיש הצוות. התשלום נפרס עד
                  סיום הקורס. חל על תשלום חד-פעמי לקורס, חוג לכל ילד ומוצרים נלווים בפריסה. הוראות
                  קבע לא מושפעות. שינוי ההגדרה חל על תקופות שכר פתוחות בלבד.
                </div>
              </SettingCard>
            )}

            {hasHugim && (
              /* ── מוצרים נלווים: the "Add-ons" on course receipts ─────── */
              <SettingCard
                id="addons"
                title="מוצרים נלווים"
                summary={[
                  addonsDefaultPercent != null ? `${addonsDefaultPercent}%` : null,
                  addonsPaymentMethod === 'next_month' ? 'תשלום בחודש העוקב' : 'פריסה לפי חודשי הקורס',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              >
                <AddonsFields />
              </SettingCard>
            )}

            {hasGroupClasses && (
              /* ── Class pay calc ─────────────────────────────────────── */
              <SettingCard id="calc" title="שיטת חישוב שכר שיעורים" summary={calcLabel}>
                <Form.Item name="class_calc_method" className="mb-0">
                  <Select
                    options={CALC_METHOD_OPTIONS as unknown as { value: string; label: string }[]}
                  />
                </Form.Item>
              </SettingCard>
            )}

            {/* ── חוג לכל ילד — only meaningful where there are חוגים to
                subsidise. Was `boxType === 'hugim'` before mechanisms became
                independently toggleable; now any box with חוגים on. ────── */}
            {hasHugim && (
              <SettingCard
                id="per_child_fee"
                title="חוג לכל ילד"
                summary={
                  perChildFeeEnabled
                    ? `${perChildFeeAmount ?? '—'} ₪ · ${perChildFeePercent ?? 0}% ממחיר החוג המלא`
                    : 'כבוי'
                }
              >
                <PerChildFeeFields form={form} />
              </SettingCard>
            )}

            {hasHugim && (
              /* ── מפגשי ניסיון: Arbox payment_type 'trial' receipts — paid
                  like any receipt, or left out of the report and the pay. ── */
              <SettingCard
                id="hugim_include_trials"
                title="מפגשי ניסיון"
                summary={hugimIncludeTrials === false ? 'לא נכללים בשכר' : 'נכללים בשכר'}
              >
                <div className="text-label text-main-text mb-2">לכלול מפגשי ניסיון בשכר חוגים?</div>
                <Form.Item name="hugim_include_trials" className="mb-0">
                  <Radio.Group className="flex flex-col gap-3">
                    <Radio value={true}>כן</Radio>
                    <Radio value={false}>לא</Radio>
                  </Radio.Group>
                </Form.Item>
              </SettingCard>
            )}

            {hasHugim && (
              /* ── מפגשים חד-פעמיים: Arbox payment_type 'onetime' receipts —
                  paid like any receipt, or left out of the report and the pay. ── */
              <SettingCard
                id="hugim_include_onetime"
                title="מפגשים חד-פעמיים"
                summary={hugimIncludeOnetime === false ? 'לא נכללים בשכר' : 'נכללים בשכר'}
              >
                <div className="text-label text-main-text mb-2">לכלול מפגשים חד-פעמיים בשכר חוגים?</div>
                <Form.Item name="hugim_include_onetime" className="mb-0">
                  <Radio.Group className="flex flex-col gap-3">
                    <Radio value={true}>כן</Radio>
                    <Radio value={false}>לא</Radio>
                  </Radio.Group>
                </Form.Item>
              </SettingCard>
            )}

            {/* ── Reply-to + accountant email ────────────────────────── */}
            <SettingCard id="send" title="שליחה ואישור">
              <Form.Item
                name="reply_to_email"
                label="כתובת להשבה (Reply-To)"
                normalize={(v) => (v === '' ? null : v)}
                rules={[{ type: 'email', message: 'כתובת אימייל לא תקינה' }]}
              >
                <Input dir="ltr" placeholder="owner@example.com" inputMode="email" />
              </Form.Item>
              <Form.Item
                name="accountant_email"
                label="מייל לשליחת דוח שכר (רו״ח / הנהח״ש)"
                normalize={(v) => (v === '' ? null : v)}
                rules={[{ type: 'email', message: 'כתובת אימייל לא תקינה' }]}
              >
                <Input dir="ltr" placeholder="accountant@example.com" inputMode="email" />
              </Form.Item>
              <Form.Item
                label="מועד אחרון לאישור והעלאת חשבונית (ימים מרגע השליחה)"
                className="mb-1"
              >
                <div className="flex items-center gap-2">
                  <Form.Item
                    name="approval_deadline_days"
                    normalize={(v) => (v === '' || v == null ? null : v)}
                    noStyle
                  >
                    <InputNumber min={1} max={90} style={{ width: 72 }} dir="ltr" placeholder="—" />
                  </Form.Item>
                  <span className="text-secondary-text">ימים</span>
                </div>
              </Form.Item>
              <div className="text-small text-secondary-text">
                איש הצוות יוכל לאשר ולהעלות חשבונית עד מספר הימים הזה מרגע שליחת הדוח. המועד המדויק (תאריך
                ושעה) מופיע במייל ובדף האישור. יש להשאיר ריק אם לא רוצים להגביל בזמן.
              </div>
            </SettingCard>

            {/* ── Coach email intro + signature ──────────────────────── */}
            <SettingCard id="coachEmail" title="תוכן המייל לאנשי הצוות" summary={coachEmailSummary}>
              <CoachEmailTextFields form={form} />
            </SettingCard>

            {/* ── חשבוניות collection policy ─────────────────────────── */}
            <SettingCard id="invoice" title="חשבוניות" summary={invoiceLabel}>
              <Form.Item name="invoice_policy" className="mb-0">
                <InvoicePolicyField />
              </Form.Item>

              {/* Amount verification — meaningless when no invoices are collected,
                  so it's hidden (NOT unmounted) while the policy is 'none'. An
                  unmounted antd Form.Item drops its value on save. */}
              <div
                className="mt-4 pt-4 border-t"
                style={{ display: invoicePolicy === 'none' ? 'none' : undefined }}
              >
                <div className="text-label font-medium text-main-text">בדיקת החשבונית</div>
                <div className="text-small text-secondary-text mt-0.5 mb-2">
                  בעת העלאת חשבונית על ידי איש הצוות, המערכת קוראת אותה ובודקת: שהסכום תואם לסכום
                  לתשלום שבדוח (כולל מע"מ, לפני ניכוי מס במקור), שהיא מופקת על מספר העוסק של איש הצוות
                  (מוגדר בהגדרות השכר הפרטניות שלו), שהיא מופנית לח״פ העסק, ושיש בה מספר הקצאה כשהחוק
                  מחייב (עוסק מורשה, מעל 5,000 ₪ לפני מע"מ). בדיקה שהמספר שלה לא הוגדר - מדולגת.
                </div>
                <Form.Item name="invoice_amount_check" className="mb-0">
                  <InvoiceAmountCheckField />
                </Form.Item>
                <div className="text-small text-main-text mt-3 mb-1">ח״פ / מספר עוסק של העסק</div>
                <Form.Item
                  name="business_id"
                  className="mb-0"
                  normalize={(v: string) => (v ?? '').replace(/\D/g, '') || null}
                  rules={[
                    {
                      validator: (_, v) =>
                        !v || isValidBusinessId(v)
                          ? Promise.resolve()
                          : Promise.reject(new Error('מספר העוסק אינו תקין')),
                    },
                  ]}
                  extra="החשבוניות צריכות להיות מופנות למספר הזה. בעסק עם יחידות רווח והפסד, ח״פ שהוגדר ליחידה גובר עליו."
                >
                  <Input dir="ltr" inputMode="numeric" placeholder="9 ספרות" style={{ width: 220 }} />
                </Form.Item>
              </div>

              <div className="flex items-start justify-between gap-4 mt-4 pt-4 border-t">
                <div>
                  <div className="text-label font-medium text-main-text">
                    העברת חשבוניות מאושרות ל-Expenses
                  </div>
                  <div className="text-small text-secondary-text mt-0.5">
                    כשאיש צוות מאשר שכר עם חשבונית מצורפת, החשבונית תיווצר אוטומטית כהוצאה בתוסף
                    ההוצאות של Arbox במידה ואתם משתמשים בו.
                  </div>
                </div>
                <Form.Item name="forward_invoices_to_expenses" valuePropName="checked" className="mb-0">
                  <Switch />
                </Form.Item>
              </div>
            </SettingCard>

            {/* ── P&L units (group-classes; self-saving, own endpoint) ── */}
            {hasGroupClasses && <PnlUnitsCard />}

            {/* ── General salary defaults (group_classes + time_clock) ──── */}
            {(hasGroupClasses || hasTimeClock) && (
              <>
                <div className="text-label font-medium text-main-text mt-4 mb-1">
                  הגדרות שכר כלליות (ברירת מחדל)
                </div>
                <div className="text-small text-secondary-text mb-3">
                  ברירת מחדל לכל אנשי הצוות. ניתן לשנות את ההגדרה פר איש צוות בלשונית "הגדרות שכר".
                </div>

                <SettingCard
                  id="base"
                  title="שכר בסיס"
                  summary={
                    baseType === 'fixed' ? 'סכום חודשי קבוע' : baseType === 'hourly' ? 'לפי שעות' : 'ללא'
                  }
                >
                  <BaseFields form={form} allowHourly={hasTimeClock} />
                </SettingCard>

                <SettingCard id="travel" title="נסיעות" summary={travelSummary}>
                  <TravelFields form={form} />
                </SettingCard>

                {hasGroupClasses && (
                  <>
                    <SettingCard id="weekend" title="תוספת סוף שבוע" summary={weekendSummary}>
                      <WeekendFields form={form} />
                    </SettingCard>

                    {/* The summary names the MODE, not on/off: "כבוי" here would
                        read as "empty classes aren't paid", the opposite of what
                        the off state does (it pays them in full). */}
                    <SettingCard
                      id="emptyClassPay"
                      title="תשלום על שיעורים ריקים"
                      summary={emptyClassPayEnabled ? 'סכום קבוע' : 'תשלום רגיל'}
                    >
                      <div className="text-small text-secondary-text mb-2">
                        איש הצוות יקבל את הסכום הקבוע הזה עבור שיעור שהתקיים אך ללא נוכחים כלל (לפי
                        שיטת חישוב השכר של העסק), במקום התשלום הרגיל על השיעור.
                      </div>
                      <EmptyClassPayFields form={form} />
                    </SettingCard>

                    <SettingCard
                      id="seriesRates"
                      title="תעריף לפי שיעור קבוע"
                      summary={
                        (seriesRates ?? []).length
                          ? `${(seriesRates ?? []).length} תעריפים מוגדרים`
                          : 'לא הוגדרו'
                      }
                    >
                      <Form.Item name="series_rates" className="mb-0">
                        <SeriesRateFields />
                      </Form.Item>
                    </SettingCard>

                    <SettingCard
                      id="categoryRates"
                      title="תעריף לפי קטגוריה"
                      summary={
                        (categoryRates ?? []).length
                          ? `${(categoryRates ?? []).length} תעריפים מוגדרים`
                          : 'לפי ארבוקס'
                      }
                    >
                      <Form.Item name="category_rates" className="mb-0">
                        <CategoryRateFields />
                      </Form.Item>
                    </SettingCard>

                    <SettingCard
                      id="volumeBonus"
                      title="בונוס לפי כמות שיעורים חודשית"
                      summary={
                        volumeBonusEnabled
                          ? `פעיל · ${(volumeBonusTiers ?? []).length} מדרגות`
                          : 'כבוי'
                      }
                    >
                      <div className="text-small text-secondary-text mb-2">
                        תוספת כספית לכל שיעור, שגדלה לפי כמות השיעורים שאיש הצוות העביר החודש. החישוב
                        הוא לפי מדרגות: כל שיעור מזכה בתעריף של המדרגה שבה הוא נמצא. נספרים שיעורים
                        שהתקיימו ושולמו עליהם — לא שיעורים ריקים ולא שיעורים שבוטלו.
                      </div>
                      <VolumeBonusFields form={form} />
                    </SettingCard>

                    <SettingCard
                      id="dailyClassAdjustment"
                      title="התאמת שכר לפי כמות שיעורים ביום"
                      summary={
                        dailyClassAdjustmentEnabled
                          ? `פעיל · ${(dailyClassAdjustmentTiers ?? []).length} מדרגות`
                          : 'כבוי'
                      }
                    >
                      <div className="text-small text-secondary-text mb-2">
                        תוספת או הורדה של סכום קבוע עבור כל שיעור, לפי מספר השיעורים במהלך אותו יום.
                        רלוונטי במידה והתעריף לשיעור משתנה לפי מספר השיעורים באותו יום. נספרים רק
                        שיעורים שהתקיימו ושולמו עליהם - לא שיעורים ריקים ולא שיעורים שבוטלו.
                      </div>
                      <DailyClassAdjustmentFields form={form} />
                    </SettingCard>

                    <SettingCard
                      id="cancelledClassPay"
                      title="תשלום על שיעורים שבוטלו"
                      summary={cancelledClassPayEnabled ? 'פעיל' : 'כבוי'}
                    >
                      <div className="text-small text-secondary-text mb-2">
                        בברירת מחדל, שיעור שבוטל אינו נחשב לאיש הצוות לצורך השכר. ההגדרה הזאת מאפשרת
                        לקבוע אחוז מהשכר שישולם בכל זאת, לפי סיבת הביטול. שיעור שבוטל אינו נספר כיום
                        עבודה ואינו מזכה בנסיעות.
                      </div>
                      <CancelledClassPayFields
                        form={form}
                        open={openCard === 'cancelledClassPay'}
                        rules={cancelRules}
                        onRulesChange={setCancelRules}
                      />
                    </SettingCard>

                    <SettingCard
                      id="conversion"
                      title="בונוס המרה מניסיון"
                      summary={conversionEnabled ? 'פעיל' : 'כבוי'}
                    >
                      <div className="text-small text-secondary-text mb-2">
                        בונוס לאיש צוות על כל מתאמן שהגיע אצלו לשיעור ניסיון והומר ללקוח, בתוך מספר
                        הימים שנקבע. הסכום לכל המרה נקבע לפי כמות ההמרות של איש הצוות בתקופה: כל
                        ההמרות מקבלות את הסכום של המדרגה שאליה הגיע. לסכום קבוע לכל המרה —
                        הגדירו מדרגה אחת החל מהמרה 1.
                      </div>
                      <ConversionFields form={form} />
                    </SettingCard>
                  </>
                )}
              </>
            )}
            </SettingsAccordion>
          </Form>
        )}
      </ConfigProvider>
    </Drawer>
  );
}
